import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Building2, Briefcase, Plus, Save, Trash2, X, Pencil, ArrowUp, ArrowDown, Type, ChevronDown, ChevronRight, Gauge, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function PlanningSettings({ settings, onChanged }) {
  return (
    <div className="space-y-8" data-testid="planning-settings">
      <ModuleTitleEditor settings={settings} onChanged={onChanged}/>
      <ProductionRolesEditor settings={settings} onChanged={onChanged}/>
      <WorkshopsEditor settings={settings} onChanged={onChanged}/>
    </div>
  );
}

// ─── Module title ────────────────────────────────────────────────────────
function ModuleTitleEditor({ settings, onChanged }) {
  const [title, setTitle] = useState(settings.module_title || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTitle(settings.module_title || ""); }, [settings.module_title]);

  const save = async () => {
    const t = title.trim();
    if (!t) { toast.error("Title cannot be empty"); return; }
    if (t === settings.module_title) return;
    setBusy(true);
    try {
      await api.patch("/planning/settings", { module_title: t });
      toast.success("Module title updated");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="pnc-card p-5 sm:p-6">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-1">
        <Type className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Module title
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Rename this module without any code changes. Shown at the top of the Planning section.
      </p>
      <div className="flex gap-2 items-end max-w-2xl">
        <div className="flex-1 space-y-1.5">
          <Label className="pnc-eyebrow">Title</Label>
          <Input
            data-testid="planning-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="planning-title-save" onClick={save} disabled={busy} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Save className="w-4 h-4" strokeWidth={2}/> Save
        </Button>
      </div>
    </div>
  );
}

// ─── Production roles ────────────────────────────────────────────────────
function ProductionRolesEditor({ settings, onChanged }) {
  const roles = settings.production_roles || [];
  const [newName, setNewName] = useState("");
  const [newPpp, setNewPpp] = useState("");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState({}); // id -> {name, panels_per_person, contractor_role_matches: str}
  const [contractorRoles, setContractorRoles] = useState([]);

  useEffect(() => {
    api.get("/reference/all").then(({ data }) => setContractorRoles(data.job_roles || []));
  }, []);

  const create = async () => {
    const name = newName.trim();
    const ppp = parseInt(newPpp, 10);
    if (!name) { toast.error("Name is required"); return; }
    if (!ppp || ppp <= 0) { toast.error("Panels/person must be a positive number"); return; }
    setBusy("create");
    try {
      await api.post("/planning/roles", { name, panels_per_person: ppp, contractor_role_matches: [] });
      toast.success(`Added "${name}"`);
      setNewName(""); setNewPpp("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const startEdit = (r) => setEditing((s) => ({ ...s, [r.id]: {
    name: r.name,
    panels_per_person: r.panels_per_person,
    contractor_role_matches: (r.contractor_role_matches || []).join(", "),
  }}));

  const cancelEdit = (id) => setEditing((s) => { const n = { ...s }; delete n[id]; return n; });

  const save = async (id) => {
    const draft = editing[id];
    const name = (draft.name || "").trim();
    const ppp = parseInt(draft.panels_per_person, 10);
    const matches = (draft.contractor_role_matches || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!name) { toast.error("Name is required"); return; }
    if (!ppp || ppp <= 0) { toast.error("Panels/person must be positive"); return; }
    setBusy(id);
    try {
      await api.patch(`/planning/roles/${id}`, {
        name, panels_per_person: ppp, contractor_role_matches: matches,
      });
      toast.success("Saved");
      cancelEdit(id);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete production role "${name}"?`)) return;
    setBusy(id);
    try {
      await api.delete(`/planning/roles/${id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const move = async (r, direction) => {
    // Swap order values with adjacent role.
    const sorted = [...roles].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === r.id);
    const partner = direction === "up" ? sorted[idx - 1] : sorted[idx + 1];
    if (!partner) return;
    setBusy(r.id);
    try {
      await Promise.all([
        api.patch(`/planning/roles/${r.id}`, { order: partner.order }),
        api.patch(`/planning/roles/${partner.id}`, { order: r.order }),
      ]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const sorted = [...roles].sort((a, b) => a.order - b.order);

  return (
    <div className="pnc-card p-5 sm:p-6">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-1">
        <Briefcase className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Production roles
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        The stages of the production line and how many panels one person completes. Link each production role to one or more <em>contractor</em> job roles so the calculator can count real people.
      </p>

      <div className="flex flex-wrap gap-2 items-end mb-6">
        <div className="space-y-1.5 flex-1 min-w-[180px]">
          <Label className="pnc-eyebrow">New role name</Label>
          <Input
            data-testid="pr-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Detail"
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <div className="space-y-1.5 w-[180px]">
          <Label className="pnc-eyebrow">Panels / person</Label>
          <Input
            data-testid="pr-new-ppp"
            type="number"
            min="1"
            value={newPpp}
            onChange={(e) => setNewPpp(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="e.g. 25"
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="pr-create-btn" onClick={create} disabled={busy === "create"} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Plus className="w-4 h-4"/> Add
        </Button>
      </div>

      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="pr-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-[130px] text-right">Panels/person</TableHead>
              <TableHead>Matches contractor roles</TableHead>
              <TableHead className="text-right w-[210px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No production roles yet.</TableCell></TableRow>
            )}
            {sorted.map((r, idx) => {
              const isEd = r.id in editing;
              const draft = editing[r.id];
              return (
                <TableRow key={r.id} data-testid={`pr-row-${r.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-muted-foreground">{r.order}</span>
                      <div className="flex flex-col">
                        <Button size="icon" variant="ghost" aria-label="Move up" disabled={idx === 0 || busy === r.id} onClick={() => move(r, "up")} className="w-5 h-5">
                          <ArrowUp className="w-3 h-3"/>
                        </Button>
                        <Button size="icon" variant="ghost" aria-label="Move down" disabled={idx === sorted.length - 1 || busy === r.id} onClick={() => move(r, "down")} className="w-5 h-5">
                          <ArrowDown className="w-3 h-3"/>
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {isEd ? (
                      <Input value={draft.name} onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...draft, name: e.target.value }}))} className="bg-[#141414] border-[#2A2A2A] max-w-[220px]"/>
                    ) : r.name}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {isEd ? (
                      <Input type="number" min="1" value={draft.panels_per_person} onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...draft, panels_per_person: e.target.value }}))} className="bg-[#141414] border-[#2A2A2A] max-w-[110px] ml-auto"/>
                    ) : r.panels_per_person}
                  </TableCell>
                  <TableCell>
                    {isEd ? (
                      <Input value={draft.contractor_role_matches} onChange={(e) => setEditing((s) => ({ ...s, [r.id]: { ...draft, contractor_role_matches: e.target.value }}))} placeholder="Comma-separated: Fit/Strip, Polisher" className="bg-[#141414] border-[#2A2A2A]"/>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(r.contractor_role_matches || []).length === 0 ? (
                          <span className="text-muted-foreground text-[12px]">—</span>
                        ) : r.contractor_role_matches.map((m) => (
                          <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-[#141414] border border-[#2A2A2A] text-[#D0D0D0]">{m}</span>
                        ))}
                      </div>
                    )}
                    {isEd && contractorRoles.length > 0 && (
                      <div className="mt-1.5 text-[11px] text-muted-foreground">
                        Available: {contractorRoles.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isEd ? (
                      <>
                        <Button size="sm" onClick={() => save(r.id)} disabled={busy === r.id} className="gap-1.5 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]" data-testid={`pr-save-${r.id}`}>
                          <Save className="w-3.5 h-3.5"/> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => cancelEdit(r.id)} data-testid={`pr-cancel-${r.id}`}>
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEdit(r)} className="gap-1.5 border-[#2A2A2A] bg-[#141414] hover:bg-[#0A0A0A]" data-testid={`pr-edit-${r.id}`}>
                          <Pencil className="w-3.5 h-3.5"/> Edit
                        </Button>
                        <Button size="sm" variant="ghost" aria-label={`Delete ${r.name}`} onClick={() => remove(r.id, r.name)} disabled={busy === r.id} className="text-[#F87171] hover:bg-[#1F0A0A]" data-testid={`pr-delete-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ─── Workshops ───────────────────────────────────────────────────────────
function WorkshopsEditor({ settings, onChanged }) {
  const workshops = settings.workshops || [];
  const productionRoles = [...(settings.production_roles || [])].sort((a, b) => a.order - b.order);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState({});
  const [departments, setDepartments] = useState([]);
  const [expanded, setExpanded] = useState({});   // {ws_id: true}
  const [ppDraft, setPpDraft] = useState({});     // {"<ws_id>:<role_id>": "45"} local input state

  useEffect(() => {
    api.get("/reference/all").then(({ data }) => setDepartments((data.departments || []).map((d) => d.name)));
  }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy("create");
    try {
      await api.post("/planning/workshops", { name, department_matches: [] });
      toast.success(`Added "${name}"`);
      setNewName("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const startEdit = (w) => setEditing((s) => ({ ...s, [w.id]: {
    name: w.name,
    department_matches: (w.department_matches || []).join(", "),
  }}));
  const cancelEdit = (id) => setEditing((s) => { const n = { ...s }; delete n[id]; return n; });

  const save = async (id) => {
    const d = editing[id];
    const name = (d.name || "").trim();
    const depts = (d.department_matches || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!name) { toast.error("Name is required"); return; }
    setBusy(id);
    try {
      await api.patch(`/planning/workshops/${id}`, { name, department_matches: depts });
      toast.success("Saved");
      cancelEdit(id);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete workshop "${name}"?`)) return;
    setBusy(id);
    try {
      await api.delete(`/planning/workshops/${id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const move = async (w, direction) => {
    const sorted = [...workshops].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === w.id);
    const partner = direction === "up" ? sorted[idx - 1] : sorted[idx + 1];
    if (!partner) return;
    setBusy(w.id);
    try {
      await Promise.all([
        api.patch(`/planning/workshops/${w.id}`, { order: partner.order }),
        api.patch(`/planning/workshops/${partner.id}`, { order: w.order }),
      ]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const sorted = [...workshops].sort((a, b) => a.order - b.order);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const savePpp = async (wsId, roleId, valueStr) => {
    const value = parseInt(valueStr, 10);
    if (!value || value <= 0) { toast.error("Panels/person must be positive"); return; }
    setBusy(`${wsId}:${roleId}`);
    try {
      await api.patch(`/planning/workshops/${wsId}/role-overrides`, {
        role_id: roleId, panels_per_person: value,
      });
      toast.success("Override saved");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const resetPpp = async (wsId, roleId) => {
    setBusy(`${wsId}:${roleId}`);
    try {
      await api.patch(`/planning/workshops/${wsId}/role-overrides`, { role_id: roleId });
      toast.success("Reset to global default");
      setPpDraft((s) => { const n = { ...s }; delete n[`${wsId}:${roleId}`]; return n; });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="pnc-card p-5 sm:p-6">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2 mb-1">
        <Building2 className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Workshops
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        A workshop groups one or more departments. Each contractor&apos;s <em>department</em> tells the calculator which workshop they belong to.
      </p>

      <div className="flex flex-wrap gap-2 items-end mb-6">
        <div className="space-y-1.5 flex-1 min-w-[240px]">
          <Label className="pnc-eyebrow">New workshop</Label>
          <Input
            data-testid="ws-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Wyton Nights + Wyton 1"
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="ws-create-btn" onClick={create} disabled={busy === "create"} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Plus className="w-4 h-4"/> Add
        </Button>
      </div>

      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="ws-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Departments</TableHead>
              <TableHead className="text-right w-[210px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No workshops yet.</TableCell></TableRow>
            )}
            {sorted.map((w, idx) => {
              const isEd = w.id in editing;
              const draft = editing[w.id];
              const isExpanded = !!expanded[w.id];
              const overrides = w.role_overrides || {};
              return (
                <Fragment key={w.id}>
                <TableRow data-testid={`ws-row-${w.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={isExpanded ? "Collapse productivity profile" : "Expand productivity profile"}
                        onClick={() => toggleExpand(w.id)}
                        data-testid={`ws-expand-${w.id}`}
                        className="w-6 h-6"
                      >
                        {isExpanded ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                      </Button>
                      <span className="font-mono text-muted-foreground">{w.order}</span>
                      <div className="flex flex-col">
                        <Button size="icon" variant="ghost" aria-label="Move up" disabled={idx === 0 || busy === w.id} onClick={() => move(w, "up")} className="w-5 h-5">
                          <ArrowUp className="w-3 h-3"/>
                        </Button>
                        <Button size="icon" variant="ghost" aria-label="Move down" disabled={idx === sorted.length - 1 || busy === w.id} onClick={() => move(w, "down")} className="w-5 h-5">
                          <ArrowDown className="w-3 h-3"/>
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {isEd ? (
                      <Input value={draft.name} onChange={(e) => setEditing((s) => ({ ...s, [w.id]: { ...draft, name: e.target.value }}))} className="bg-[#141414] border-[#2A2A2A] max-w-[280px]"/>
                    ) : w.name}
                  </TableCell>
                  <TableCell>
                    {isEd ? (
                      <Input value={draft.department_matches} onChange={(e) => setEditing((s) => ({ ...s, [w.id]: { ...draft, department_matches: e.target.value }}))} placeholder="Comma-separated: MG, Wyton 1" className="bg-[#141414] border-[#2A2A2A]"/>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(w.department_matches || []).length === 0 ? (
                          <span className="text-muted-foreground text-[12px]">—</span>
                        ) : w.department_matches.map((d) => (
                          <span key={d} className="text-[11px] px-1.5 py-0.5 rounded bg-[#141414] border border-[#2A2A2A] text-[#D0D0D0]">{d}</span>
                        ))}
                      </div>
                    )}
                    {isEd && departments.length > 0 && (
                      <div className="mt-1.5 text-[11px] text-muted-foreground">
                        Available: {departments.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isEd ? (
                      <>
                        <Button size="sm" onClick={() => save(w.id)} disabled={busy === w.id} className="gap-1.5 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]" data-testid={`ws-save-${w.id}`}>
                          <Save className="w-3.5 h-3.5"/> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => cancelEdit(w.id)} data-testid={`ws-cancel-${w.id}`}>
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEdit(w)} className="gap-1.5 border-[#2A2A2A] bg-[#141414] hover:bg-[#0A0A0A]" data-testid={`ws-edit-${w.id}`}>
                          <Pencil className="w-3.5 h-3.5"/> Edit
                        </Button>
                        <Button size="sm" variant="ghost" aria-label={`Delete ${w.name}`} onClick={() => remove(w.id, w.name)} disabled={busy === w.id} className="text-[#F87171] hover:bg-[#1F0A0A]" data-testid={`ws-delete-${w.id}`}>
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow key={`${w.id}-profile`} data-testid={`ws-profile-${w.id}`}>
                    <TableCell colSpan={4} className="bg-[#141414] border-t-0">
                      <div className="py-3 px-1">
                        <div className="flex items-center gap-2 mb-3">
                          <Gauge className="w-4 h-4 text-[#C0C0C0]"/>
                          <div className="pnc-eyebrow">Productivity profile — {w.name}</div>
                        </div>
                        <p className="text-[11px] text-muted-foreground mb-3">
                          Override panels/person for this workshop. Blank = use the global default. All calculations for this workshop use the override when set.
                        </p>
                        {productionRoles.length === 0 ? (
                          <div className="text-[13px] text-muted-foreground py-4">
                            No production roles defined yet — add roles above first.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {productionRoles.map((role) => {
                              const key = `${w.id}:${role.id}`;
                              const currentOverride = overrides[role.id];
                              const draftVal = ppDraft[key] !== undefined
                                ? ppDraft[key]
                                : (currentOverride ?? "");
                              const isOverridden = currentOverride !== undefined && currentOverride !== null;
                              return (
                                <div key={role.id} className="rounded-lg border border-[#2A2A2A] bg-[#1E1E1E] p-3">
                                  <div className="flex items-center justify-between mb-1.5">
                                    <div className="text-[13px] text-[#F5F5F5] font-medium">{role.name}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">
                                      default {role.panels_per_person}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Input
                                      type="number"
                                      min="1"
                                      inputMode="numeric"
                                      placeholder={`${role.panels_per_person}`}
                                      value={draftVal}
                                      onChange={(e) => setPpDraft((s) => ({ ...s, [key]: e.target.value.replace(/[^\d]/g, "") }))}
                                      onKeyDown={(e) => { if (e.key === "Enter" && draftVal) savePpp(w.id, role.id, draftVal); }}
                                      className={`bg-[#141414] border-[#2A2A2A] font-mono ${isOverridden ? "text-[#C0C0C0]" : "text-[#6B6B6B]"}`}
                                      data-testid={`ws-ppp-${w.id}-${role.name}`}
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => savePpp(w.id, role.id, draftVal)}
                                      disabled={!draftVal || busy === key}
                                      className="h-9 px-2 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]"
                                      aria-label={`Save ${role.name} override`}
                                      data-testid={`ws-ppp-save-${w.id}-${role.name}`}
                                    >
                                      <Save className="w-3.5 h-3.5"/>
                                    </Button>
                                    {isOverridden && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => resetPpp(w.id, role.id)}
                                        disabled={busy === key}
                                        aria-label={`Reset ${role.name} to global default`}
                                        data-testid={`ws-ppp-reset-${w.id}-${role.name}`}
                                        className="h-9 px-2 text-[#A8A8A8] hover:text-[#F5F5F5]"
                                      >
                                        <RotateCcw className="w-3.5 h-3.5"/>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
